import React, { useState, useEffect } from 'react';
import { deviceAPI, assignmentAPI, employeeAPI } from '../services/api';

const DeviceSearch = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [devices, setDevices] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (searchQuery.trim()) {
      handleSearch();
    } else {
      loadInitialData();
    }
  }, [searchQuery]);

  const loadInitialData = async () => {
    try {
      const [devicesRes, assignmentsRes, employeesRes] = await Promise.all([
        deviceAPI.getAll(),
        assignmentAPI.getAll(),
        employeeAPI.getAll()
      ]);
      
      setDevices(devicesRes.data);
      setAssignments(assignmentsRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    try {
      const response = await deviceAPI.search(searchQuery);
      setDevices(response.data);
    } catch (error) {
      console.error('Search failed:', error);
      setDevices([]);
    } finally {
      setLoading(false);
    }
  };

  const getAssignedEmployee = (assetId) => {
    const assignment = assignments.find(
      a => a.deviceId === assetId && a.status === 'active'
    );
    if (assignment) {
      const employee = employees.find(e => e.id === assignment.employeeId);
      return employee ? employee.name : 'Unknown';
    }
    return null;
  };

  const getStatusBadgeClass = (status) => {
    if (status === 'available') return 'status-available';
    if (status === 'assigned') return 'status-assigned';
    return 'status-maintenance';
  };

  return (
    <div className="card">
      <h2>Search Devices</h2>
      
      <input
        type="text"
        className="search-input"
        placeholder="Search by Asset ID, Device Name, Model, Device Type, OS Version, Color, RAM, UDID, Screen Resolution, Purchase Info, or Description..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />

      {loading ? (
        <p>Searching...</p>
      ) : (
        <>
          <p style={{ marginBottom: '1rem', color: '#666' }}>
            {devices.length} device(s) found
          </p>
          
          {devices.length === 0 ? (
            <p>No devices found. Try a different search term.</p>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Asset ID</th>
                    <th>Device Name</th>
                    <th>Device Type</th>
                    <th>Model</th>
                    <th>OS Version</th>
                    <th>Condition</th>
                    <th>Status</th>
                    <th>Assigned To</th>
                  </tr>
                </thead>
                <tbody>
                  {devices.map(device => {
                    const assignedEmployee = getAssignedEmployee(device.assetId);
                    return (
                      <tr key={device.assetId}>
                        <td>{device.assetId || '-'}</td>
                        <td>{device.deviceName || device.model || '-'}</td>
                        <td>{device.deviceType || device.type || '-'}</td>
                        <td>{device.model || '-'}</td>
                        <td>{device.osVersion || '-'}</td>
                        <td>
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '20px',
                            fontSize: '0.85rem',
                            fontWeight: '500',
                            backgroundColor: device.condition === 'GOOD' ? '#d4edda' : '#f8d7da',
                            color: device.condition === 'GOOD' ? '#155724' : '#721c24'
                          }}>
                            {device.condition || '-'}
                          </span>
                        </td>
                        <td>
                          <span className={`status-badge ${getStatusBadgeClass(device.status)}`}>
                            {device.status}
                          </span>
                        </td>
                        <td>{assignedEmployee || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DeviceSearch;
